import { Box, Button, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CaptureTasksDialog } from './capture';
import { useKeyboardNav, usePlanEditor } from './editor';
import { EmptyPlanState } from './EmptyPlanState';
import { PlanTree } from './PlanTree';
import { PlanViewProvider } from './PlanViewContext';
import { isPlanView, PlanViewSwitcher } from './PlanViewSwitcher';
import type { PlanView } from './PlanViewSwitcher';
import { PlanTimeline } from './timeline';
import { DEPTH_BASE_PX } from './treeGeometry';
import { useDeselectOnOutsideClick } from './useDeselectOnOutsideClick';

import { MaterialSymbol } from '@/components/MaterialSymbol';
import { SECTION_HEADER_SX } from '@/components/ui';
import { usePersistedState } from '@/lib';

/**
 * The main "Project plan" panel: title, meta chip, add buttons, the tree.
 *
 * The surrounding content column (max width, padding, scroll) belongs to
 * `PanelContent` now, so this component no longer positions itself on the page.
 */
export const PlanPanel = () => {
  const { t } = useTranslation(['pmPlanning', 'projectManagement']);
  const { flat, state, actions } = usePlanEditor();
  const [captureOpen, setCaptureOpen] = useState(false);
  /**
   * View mode is a preference, not document state: keeping it in the editor
   * reducer would mean every inbound sync (RESET_FROM_SERVER / ADOPT_SERVER)
   * snapped the user back to the list.
   */
  const [view, setView] = usePersistedState<PlanView>('pm.planning.planView', 'list', isPlanView);
  /*
    The card below is the page's scroller, so the header and this toolbar stay
    put while the rows move — the Project Portfolio shape.

    State, not a ref: the virtualiser has to re-render once the node exists, and
    a ref assigned during render is still null when the child's mount effect
    reads it. This used to live on `PlanningPage` and point at `PanelContent`;
    it moved here with the scroll itself, since nothing else on the page wants
    it.
  */
  const [scrollElement, setScrollElement] = useState<HTMLElement | null>(null);
  useKeyboardNav({ enabled: !captureOpen });
  useDeselectOnOutsideClick();
  const { totalCount } = flat.index;
  const isEmpty = totalCount === 0;

  return (
    /*
      The provider only lifts `view` into reach for the date picker's
      "Set dates in the timeline" link, four levels down. `PlanPanel` still owns
      the state; see the note on `PlanViewContext`.
    */
    <PlanViewProvider view={view} setView={setView}>
      <Box
        data-automation-id="plan-panel"
        sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}
      >
        <Stack direction="row" sx={SECTION_HEADER_SX}>
          <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
            {/*
              h4, not display2: the project title above is the page's 20px navy
              heading, and a second one here competed with it for the same level.
              Web uses h4 for PageSection titles.
            */}
            <Typography variant="h4" component="h2">
              {t('projectManagement:projectPlan.PageTitle')}
            </Typography>
            {!isEmpty && <PlanViewSwitcher value={view} onChange={setView} />}
          </Stack>
          {/*
            Hidden while the plan is empty: the empty state below offers the same
            two actions, and duplicating them in the toolbar put four competing
            calls to action on a page with nothing on it. Once there is a plan,
            the toolbar is the only place they live.
          */}
          {isEmpty ? null : (
            <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<MaterialSymbol name="edit_note" size={16} />}
                data-automation-id="capture-tasks-button"
                onClick={() => setCaptureOpen(true)}
              >
                {t('planPanel.CaptureTasks')}
              </Button>
              <Button
                variant="contained"
                startIcon={<MaterialSymbol name="add" size={16} />}
                data-automation-id="add-task-button"
                // Always a top-level task, whichever row is active: nesting under the
                // selection read as "add subtask". Nesting stays on the row kebab and
                // Ctrl/⌘+Enter.
                onClick={() => actions.addTask(null)}
              >
                {t('projectManagement:projectPlan.actions.AddTask')}
              </Button>
            </Stack>
          )}
        </Stack>

        <Box
          ref={setScrollElement}
          data-automation-id="plan-scroller"
          sx={{
            bgcolor: 'var(--bg-panel)',
            borderRadius: 'var(--border-radius-medium)',
            border: 'var(--border-width-hairline) solid var(--border-subtle)',
            /*
              `auto`, not `hidden`: this box is what scrolls now. It still clips
              to the rounded corners, and making it the scrollport is also what
              finally makes the tree's column header and the timeline's month band
              stick — both were `position: sticky` against a box that never
              scrolled, so neither did anything.
            */
            overflow: 'auto',
            /*
              An empty plan HUGS its empty state instead of stretching, matching
              the Financials empty state. Only a plan with rows takes the rest of
              the panel: a full-height bordered box around one short message is a
              lot of nothing, and the two empty states are supposed to share a
              shape (they are pinned to the same y and height by
              `financials-contracts-crud.spec.ts`).
            */
            ...(isEmpty ? {} : { flex: 1, minHeight: 0 }),
          }}
        >
          {isEmpty ? (
            <EmptyPlanState
              onStartPlanning={actions.startPlanning}
              onCaptureTasks={() => setCaptureOpen(true)}
            />
          ) : view === 'timeline' ? (
            // Keyed on the project so the timeline's window seed is re-captured
            // rather than inherited when a different project loads.
            <PlanTimeline key={state.projectId} scrollElement={scrollElement} />
          ) : (
            <>
              <PlanTree scrollElement={scrollElement} />
              <Box
                component="button"
                type="button"
                data-automation-id="ghost-add-task-row"
                onClick={() => actions.addTask(null)}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 'var(--spacing-unit-1)',
                  /*
                    Hugs its label rather than spanning the list: the pill is
                    the affordance, and a full-width one made the hover read as
                    a row of the table rather than a button under it.

                    The offset keeps the content starting at `DEPTH_BASE_PX`,
                    where the old full-width row's `pl` put it — minus the
                    pill's own left padding, so the padding stays even on all
                    four sides and the label does not drift right. `mt`/`mb`
                    keep it clear of the last row's border instead of sitting
                    against it.
                  */
                  ml: `calc(${String(DEPTH_BASE_PX)}px - var(--spacing-unit-2))`,
                  mt: 'var(--spacing-unit-1)',
                  mb: 'var(--spacing-unit-1)',
                  px: 'var(--spacing-unit-2)',
                  py: 'var(--spacing-unit-1)',
                  border: 'none',
                  bgcolor: 'transparent',
                  fontSize: 'var(--font-size-label)',
                  fontFamily: 'var(--font-family)',
                  color: 'var(--fg-3)',
                  cursor: 'pointer',
                  /*
                    A fog pill on hover, not a pink full-bleed band: this row
                    adds a task, it is not the page's call to action — the pink
                    "Add task" button in the toolbar is. Pink here made the
                    ghost row read as the primary one.
                  */
                  borderRadius: 'var(--border-radius-pill)',
                  '&:hover': { bgcolor: 'var(--color-fog-20)', color: 'var(--color-fog-100)' },
                  '&:focus-visible': { outline: 'var(--focus-ring)', outlineOffset: '-2px' },
                }}
              >
                <MaterialSymbol name="add" size={16} />
                {t('projectManagement:projectPlan.actions.AddTask')}
              </Box>
            </>
          )}
        </Box>

        <CaptureTasksDialog open={captureOpen} onClose={() => setCaptureOpen(false)} />
      </Box>
    </PlanViewProvider>
  );
};
